import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query"
import { AxiosError } from "axios"

import { API_DEFAULT } from "src/constants/minutApi"
import { queryKeys } from "src/data/ecommerce/ecommerceQueryCache"
import {
  TFetchAvailableShippingMethodsRequestBody,
  TFetchAvailableShippingMethodsResponse,
  TFetchProducts400Response,
  TFetchProductsQuery,
  TFetchProductsResponse,
  TFetchShippingDestinationsResponse,
  TFetchWarehousesResponse,
} from "src/data/ecommerce/ecommerceTypes"
import { useMinutPatch, useMinutQuery } from "src/hooks/minutQuery"
import { minutApiHttpClient } from "src/utils/minutApiHttpClient"

export function useFetchShippingDestinations(
  options?: UseQueryOptions<
    TFetchShippingDestinationsResponse,
    AxiosError,
    TFetchShippingDestinationsResponse,
    ReturnType<typeof queryKeys.shippingDestinations>
  >
) {
  async function fetchShippingDestinations() {
    const response = await minutApiHttpClient.get(
      `${API_DEFAULT}/ecommerce/shipping_methods/destinations`
    )
    return response.data
  }

  return useQuery({
    queryKey: queryKeys.shippingDestinations(),
    queryFn: fetchShippingDestinations,
    ...options,
  })
}

export function useFetchProducts({
  userId,
  filter,
  options,
}: {
  userId?: string
  filter: TFetchProductsQuery
  options?: UseQueryOptions<
    TFetchProductsResponse,
    AxiosError<TFetchProducts400Response>,
    TFetchProductsResponse,
    ReturnType<typeof queryKeys.products>
  >
}) {
  async function fetchPoruducts() {
    if (!userId) {
      const response = await minutApiHttpClient.get(
        `${API_DEFAULT}/ecommerce/products`,
        { params: filter }
      )
      return response.data
    }

    const response = await minutApiHttpClient.get(
      `${API_DEFAULT}/users/${userId}/products`,
      { params: filter }
    )
    return response.data
  }

  return useQuery({
    queryKey: queryKeys.products(filter),
    queryFn: fetchPoruducts,
    ...options,
  })
}

export function useFetchShippingMethods({
  body,
  options,
}: {
  body: TFetchAvailableShippingMethodsRequestBody
  options?: UseQueryOptions<
    TFetchAvailableShippingMethodsResponse,
    AxiosError,
    TFetchAvailableShippingMethodsResponse,
    ReturnType<typeof queryKeys.shippingMethods>
  >
}) {
  async function fetchShippingMethods() {
    const response = await minutApiHttpClient.post(
      `${API_DEFAULT}/ecommerce/cart_estimate/shipping_methods`,
      body
    )
    return response.data
  }

  return useQuery({
    queryKey: queryKeys.shippingMethods(body),
    queryFn: fetchShippingMethods,
    ...options,
  })
}

export function useFetchWarehouses(args?: {
  options?: UseQueryOptions<
    TFetchWarehousesResponse,
    AxiosError,
    TFetchWarehousesResponse,
    ReturnType<typeof queryKeys.warehouses>
  >
}) {
  return useMinutQuery({
    queryKey: queryKeys.warehouses(),
    queryPath: "/ecommerce/warehouses",
    ...args?.options,
  })
}

export function usePatchWarehouse() {
  const queryClient = useQueryClient()

  return useMinutPatch<"/ecommerce/warehouses/{warehouse_id}">({
    apiPath: "/ecommerce/warehouses/{warehouse_id}",
    pathFn: ({ warehouse_id }) => `/ecommerce/warehouses/${warehouse_id}`,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.warehouses() })
      },
    },
  })
}
